import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Alert, Button, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from './axios';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setError('Verifieringslänk saknas eller är ogiltig.');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/verify-email/${token}`);
        
        if (response.data.success) {
          setSuccess(true);
        } else {
          setError(response.data.message || 'Verifieringen misslyckades.');
        }
      } catch (error) {
        console.error('Verify email error:', error);
        if (error.response && error.response.data && error.response.data.message) {
          setError(error.response.data.message);
        } else {
          setError('Ett fel uppstod vid verifiering. Länken kan vara ogiltig eller utgången.');
        }
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token]);

  // Show loading spinner while verifying
  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Verifierar din e-postadress...</Typography>
      </Container>
    );
  }

  // Show success message
  if (success) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ 
          backgroundColor: '#fff', 
          borderRadius: 3, 
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
          padding: { xs: '24px', md: '40px' },
        }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: '#1A237E',
              marginBottom: '16px',
              textAlign: 'center',
            }}
          >
            E-post verifierad!
          </Typography>
          <Alert severity="success" sx={{ marginBottom: '24px' }}>
            Din e-postadress har verifierats. Du kan nu logga in på ditt konto.
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/info')}
            sx={{
              backgroundColor: '#1565C0',
              color: '#fff',
              textTransform: 'none',
              padding: '12px',
              fontSize: '1rem',
              fontWeight: 500,
              borderRadius: 2,
              '&:hover': {
                backgroundColor: '#0d47a1',
              },
            }}
          >
            Gå till inloggning
          </Button>
        </Box>
      </Container>
    );
  }

  // Show error message
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Box sx={{ 
        backgroundColor: '#fff', 
        borderRadius: 3, 
        boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
        padding: { xs: '24px', md: '40px' },
      }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: '#1A237E',
            marginBottom: '16px',
            textAlign: 'center',
          }}
        >
          Verifiering misslyckades
        </Typography>
        <Alert severity="error" sx={{ marginBottom: '24px' }}>
          {error}
        </Alert>
        <Typography
          variant="body2"
          sx={{
            color: '#666',
            marginBottom: '24px',
            textAlign: 'center',
          }}
        >
          Om din länk har gått ut kan du begära en ny verifieringslänk genom att försöka logga in.
        </Typography>
        <Button
          variant="contained"
          fullWidth
          onClick={() => navigate('/info')}
          sx={{
            backgroundColor: '#1565C0',
            color: '#fff',
            textTransform: 'none',
            padding: '12px',
            fontSize: '1rem',
            fontWeight: 500,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#0d47a1',
            },
          }}
        >
          Tillbaka till startsidan
        </Button>
      </Box>
    </Container>
  );
}
